import {
  CAT_CITY,
  CAT_LINE,
  CAT_TYPE,
  Facility,
  Filter,
  cat,
  filterFacilities,
  loadFacilities,
} from './lib/facilities';
import { $, $$, $1, $e, $n, template } from './lib/utils';

const PER_PAGE = 30;

const filterJoin = (ary: unknown[], sep?: string) => ary.filter((v) => v).join(sep || '');
const makeTitle = (titles: Iterable<string | null | undefined> | null | undefined, wrapAlways = true) => {
  if (!titles) return;
  const titlesAry = [...titles].filter((str) => str);
  if (titlesAry.length === 0) return;
  const title = titlesAry.join(',');
  return wrapAlways || titlesAry.length > 1 ? `(${title})` : title;
};
const setTitle = <E extends Document | HTMLElement>(
  element: E | undefined | null,
  attrName: E extends Document ? 'title' : E extends HTMLMetaElement ? 'content' : 'textContent',
  title: string,
) => {
  if (element) element[attrName] = element[attrName]?.replace(/^.*施設一覧/, title) ?? title;
};

(async () => {
  const data = await loadFacilities();
  const search = new URLSearchParams(location.search);
  const presearch = (() => {
    try {
      return JSON.parse($('search-json').innerHTML);
    } catch (_err) {
      return;
    }
  })();
  const filter: Filter = {
    pref: search.get('pref') ?? presearch?.pref,
    area: search.get('area') ?? presearch?.area,
    city: search.get('city')?.split(',') ?? presearch?.city,
    line: search.get('line')?.split(',') ?? presearch?.line,
    type: search.get('type')?.split(','),
    feat: search.get('feat')?.split(','),
  };
  if (Object.values(filter).every((value) => !value)) {
    location.replace('/search/');
    return;
  }

  const pref = cat(data, CAT_CITY, filter.pref);
  const area = cat(data, CAT_LINE, filter.area);
  const city = pref?.children?.filter(({ slug }) => filter.city?.includes(slug)).map(({ name }) => name);
  const line = area?.children?.filter(({ slug }) => filter.line?.includes(slug)).map(({ name }) => name);
  const type = data.categories[CAT_TYPE].filter(({ slug }) => filter.type?.includes(slug)).map(({ name }) => name);

  const title =
    filterJoin([
      pref?.name,
      makeTitle(city, false),
      area && !line?.length ? area.name : makeTitle(line, false),
      makeTitle(type),
      makeTitle(filter.feat),
    ]) + 'の施設一覧';
  setTitle(document, 'title', title);
  $$<HTMLMetaElement>('meta[content]').forEach((meta) => setTitle(meta, 'content', title));
  ['#page_title h1', '[itemtype="http://schema.org/ListItem"]:last-child [itemprop=name]'].forEach((selector) =>
    setTitle($1<HTMLElement>(selector), 'textContent', title),
  );
  $1<HTMLMetaElement>('meta[property="og:url"]')!.content = (() => {
    const search2 = new URLSearchParams(search);
    search2.delete('p');
    return new URL(`${location.pathname}?${search2}`, location.href).toString();
  })();

  const facilities = filterFacilities(data, filter);
  const totalPage = Math.ceil(facilities.length / PER_PAGE);
  const initialPage = Math.min(Math.max(+(search.get('p') || 1), 1), totalPage);
  if (!history.state) history.replaceState({ page: initialPage }, '');

  if (totalPage <= 1) {
    $1('.list_btm')!.hidden = true;
  }
  if (totalPage === 0) {
    $1('.list_result')!.textContent = '条件に一致する施設は見つかりませんでした。';
    return;
  }

  const tmpl = template('facility-template');
  const loadFacility = async (div: HTMLElement, facility: Facility) => {
    const res = await fetch(`/search/+list/${facility.id}.html`);
    div.innerHTML = await res.text();
  };

  const buildResult = (page: number) => {
    const currentPage = page;
    const offset = (page - 1) * PER_PAGE;
    const showFacilities = facilities.slice(offset, offset + PER_PAGE);

    const target = $1('#ch_feature .list_wrap')!;
    $('list-total').textContent = $('list-total-top').textContent = $n(facilities.length);
    $('list-show').textContent = $('list-show-top').textContent = `${offset + 1}-${offset + showFacilities.length}`;
    target.replaceChildren(
      ...showFacilities.map((facility) => {
        const div = tmpl(facility);
        loadFacility(div, facility);
        return div;
      }),
    );

    const pagination: (Node | string)[] = [];
    const pageLink = (title: string | number, page: number, className?: string) => {
      const search2 = new URLSearchParams(search);
      if (page === 1) search2.delete('p');
      else search2.set('p', `${page}`);
      const href = location.pathname + (search2.size ? `?${search2}` : '');
      const a = $e('a', { className, href }, `${title}`);
      a.addEventListener('click', (ev) => {
        ev.preventDefault();
        if (page !== currentPage) {
          history.pushState({ page }, '', href);
          buildResult(page);
          scrollTo({ top: 0, behavior: 'instant' });
        }
      });
      return a;
    };
    if (page > 1) {
      pagination.push(pageLink('最初', 1, 'pgn_first'), pageLink('前へ', page - 1, 'pgn_prev'));
    }
    pagination.push(
      ...Array.from({ length: totalPage }).map((_, p) =>
        pageLink(p + 1, p + 1, p + 1 === page ? 'pgn_current' : undefined),
      ),
    );
    if (page < totalPage) {
      pagination.push(pageLink('次へ', page + 1, 'pgn_next'), pageLink('最後', totalPage, 'pgn_fine'));
    }
    $1('.list_pagenate')!.replaceChildren(...pagination);
  };
  buildResult(initialPage);
  document.querySelector('footer').style.display = 'block';
  addEventListener('popstate', (ev) => {
    const page = ev.state?.page ?? initialPage;
    if (typeof page === 'number' && page >= 1 && page <= totalPage) {
      buildResult(page);
    }
  });
})();
